// ═══════════════════════════════════════════════════════════════
// TAREAS — Sistema unificado (v2)
// ═══════════════════════════════════════════════════════════════
//
// ⚠ NAMING — leer antes de tocar:
//
//   En este proyecto coexisten 3 entidades DISTINTAS cuyos nombres
//   se cruzan por motivos históricos. NO son sinónimos:
//
//   1. Accionable (catálogo metodológico de Frameworks)
//      → vive en `/accionables` top-level + `/catalogoAccionables`.
//      → es un item del playbook consultivo. NO es una tarea.
//
//   2. Tarea (lo que maneja este archivo) — "el trabajo a hacer"
//      → vive físicamente en `empresas/{empId}/tareas/{id}` (SUB abajo).
//        ⚠ ADR 044 (6-jul-2026): este comentario decía `asignaciones` y
//        MENTÍA — el código siempre escribió `tareas`. El backend se
//        construyó siguiendo el comentario y operó meses contra una
//        subcolección muerta. El path del backend ahora vive en
//        functions/lib/tareasRefs.js (espejo de SUB). Si tocás el path,
//        tocás LOS DOS y verificás contra Firestore real, no contra docs.
//      → la función canónica de creación se llama `crearAccionable`
//        por razones históricas. Se renombrará a `crearTarea` en F11
//        del plan Postventa (ver docs/postventa-mvp-plan.md).
//      → aliases existentes para compat: `crearAsignacion` (deprecado).
//
//   3. Asignación (scope user) — vínculo userId → empresas visibles
//      → vive en `/asignaciones/{aid}` top-level.
//      → NÚCLEO de Usuarios v2. Ver `asignacionesV2.js`.
//      → NO confundir con la subcolección legacy de tareas.
//
//   Detalle completo: ver `docs/modelo-tareas.md` (Parte A del plan).
//
// Unifica los dos sistemas anteriores:
//   - Viejo: colección top-level `accionables` (consultoría, mensajes, ClarIA).
//   - Nuevo: subcollección `empresas/{empId}/asignaciones` (dashboards, reglas).
// Ambos se persistieron y se unificaron en la subcolección
// `empresas/{empId}/asignaciones/{id}` (la rama "Nuevo" ganó).
//
// Flujo canónico:
//   1. Alguien crea un accionable (PROPUESTO si requiere aceptación, PENDIENTE si directo).
//      Puede venir de: mensaje, warning, dashboard, reunión, ClarIA, regla, o manual.
//   2. Si PROPUESTO → responsable ACEPTA (→ PENDIENTE) o RECHAZA (→ vuelve al emisor).
//   3. El responsable trabaja: PENDIENTE → NO_INICIADA → EN_CURSO → EN_REVISION → CERRADO.
//   4. Transferencia = escalado con aceptación:
//      - Transferir → estado TRANSFERIDO → nuevo responsable acepta o rechaza.
//   5. Delegación (sin aceptación): reasigna directo, con máximo configurable.
//   6. Pausa/Reanudación: PAUSADA ↔ EN_CURSO.
//   7. Cancelación: cualquier estado activo → CANCELADA (irreversible salvo reabrir).
//   8. Vencimiento: si pasa el deadline sin completar → VENCIDA (automático o manual).
//
// Campos del documento:
//   {
//     id, empresaId,
//     titulo, descripcion,
//     // ── Personas ──
//     emisorUserId,            // quien creó el accionable
//     responsableUserId,       // responsable actual (cambia al transferir/rechazar)
//     responsableAnterior,     // último responsable antes del cambio
//     ownerUserId,             // dueño para scope='propio' (siempre el creador)
//     asignadoA,               // alias de responsableUserId (compat con sistema nuevo)
//     asignadoPor,             // alias de emisorUserId (compat con sistema nuevo)
//     participantes[],         // todos los que participaron alguna vez
//     // ── Estado y prioridad ──
//     estado, prioridad,
//     vencimiento,             // Date (viejo) — se mantiene por compat
//     deadline,                // Timestamp (nuevo) — preferido
//     slaId,
//     // ── Origen y contexto ──
//     origen,                  // string|object: 'manual'|'regla'|'dashboard_publicidad'|{tipo:'mensaje',...}
//     origenDashboard,         // { modulo, seccion, empresaId, fechaAnalisis }
//     area,                    // 'publicidad'|'stock'|'ventas'|'preguntas'|'general'
//     tipoAccion,              // 'pausar_publicacion'|'activar_publicacion'|etc.
//     // ── Recursos vinculados ──
//     recursos[],              // [{ itemId, titulo, permalink, metricas:{} }]
//     recurso,                 // objeto simple (compat viejo, singular)
//     señalId,                 // FK a señal que lo generó (si aplica)
//     // ── Delegación y escalamiento ──
//     delegaciones,            // contador de delegaciones (máx configurable)
//     escalamiento,            // { tipo, escalaA, recordatorioHoras }
//     // ── Verificación ──
//     requiereVerificacion,
//     verificada,
//     // ── Flags ──
//     iaResuelta,              // true si ClarIA ya procesó la tarea
//     motivoRechazo,
//     motivoPausa,
//     // ── Timestamps ──
//     aceptadoEn, completadaEn, cerradoEn, canceladaEn,
//     creadoEn, actualizadoEn,
//     // ── Historial ──
//     historial[]              // [{ accion, porUserId|por, en|fecha, nota?, datos? }]
//   }
//
// Subcollecciones:
//   empresas/{empId}/asignaciones/{id}/comentarios/{cId}
//   (físicamente la ruta dice "asignaciones" por legacy histórico —
//    ver bloque ⚠ NAMING arriba).
//
// Para vista cross-empresa del consultor: collectionGroup queries.
// ═══════════════════════════════════════════════════════════════

import {
  collection, doc, getDoc, getDocs, setDoc, updateDoc, deleteDoc, addDoc,
  query, where, orderBy, serverTimestamp, writeBatch, collectionGroup,
  arrayUnion, runTransaction,
} from 'firebase/firestore'
import { db } from './firebase'
import { docConId } from './docConId'
import { COLLECTIONS } from './constants'
import { forUpdate } from './dbHelpers'
import { mergearRecursosPorOrigen, recursoConOrigen, ESTADOS_TAREA_ACTIVA } from './mergeRecursos'
import { getTipoTareaPorCodigo, resolverSLA, calcularTrackingUpdates } from './tiposTarea'
import {
  notificar, NOTIFICACION_TIPOS, NOTIFICACION_PRIORIDADES,
} from './notificaciones'
// El link de una notificación de tarea NO se arma a mano acá (report
// lxBaLYCgFXIpSQuO6qDR: los cuatro se armaban con un id de GRUPO donde va un id
// de tab y con un param que nadie leía). Una sola fuente por lado del cable.
import { linkTareaNotificacion } from './tareaLinkNotificacion'
import { estadoOperable, CAUSAS_OPERABLE } from './moduloContratado'

// ═══════════════════════════════════════════════════════════════
// ESTADOS
// ═══════════════════════════════════════════════════════════════

export const ACCIONABLE_ESTADOS = {
  PROPUESTO:     'propuesto',     // Esperando aceptación del responsable
  PENDIENTE:     'pendiente',     // Aceptado, aún no arrancó
  NO_INICIADA:   'no_iniciada',   // Aceptada pero sin empezar
  EN_CURSO:      'en_curso',      // Trabajándola
  EN_REVISION:   'en_revision',   // Lista para revisar
  CERRADO:       'cerrado',       // Terminada
  RECHAZADO:     'rechazado',     // Responsable rechazó
  TRANSFERIDO:   'transferido',   // Escalada, esperando aceptación del nuevo
  // Estados del sistema nuevo (aliases para compat)
  EN_PROGRESO:   'en_curso',      // alias → en_curso
  COMPLETADA:    'cerrado',       // alias → cerrado
  PAUSADA:       'pausada',       // Pausada temporalmente
  VENCIDA:       'vencida',       // Pasó el deadline
  CANCELADA:     'cancelada',     // Cancelada
}

export const ESTADOS_LISTA = [
  { id: 'propuesto',   label: 'Propuesto',    color: '#0ea5e9' },
  { id: 'pendiente',   label: 'Pendiente',    color: '#eab308' },
  { id: 'no_iniciada', label: 'No iniciada',  color: '#f97316' },
  { id: 'en_curso',    label: 'En curso',     color: '#3b82f6' },
  { id: 'en_revision', label: 'En revisión',  color: '#8b5cf6' },
  { id: 'cerrado',     label: 'Cerrado',      color: '#22c55e' },
  { id: 'rechazado',   label: 'Rechazado',    color: '#f43f5e' },
  { id: 'transferido', label: 'Transferido',  color: '#d946ef' },
  { id: 'pausada',     label: 'Pausada',      color: '#6b7280' },
  { id: 'vencida',     label: 'Vencida',      color: '#ef4444' },
  { id: 'cancelada',   label: 'Cancelada',    color: '#6b7280' },
]

export const PRIORIDADES = [
  { id: 'critica', label: 'Crítica', color: '#ef4444' },
  { id: 'alta',    label: 'Alta',    color: '#f59e0b' },
  { id: 'media',   label: 'Media',   color: '#3b82f6' },
  { id: 'normal',  label: 'Normal',  color: '#3b82f6' },  // alias de media
  { id: 'baja',    label: 'Baja',    color: '#6b7280' },
]

export const AUTONOMIA_NIVELES = [
  { id: 'manual', label: 'Manual', desc: 'Siempre genera señal — humano aprueba' },
  { id: 'semi', label: 'Semi-automático', desc: 'Si confianza IA > umbral → directo; sino → señal' },
  { id: 'auto', label: 'Automático', desc: 'Crea accionable directo sin aprobación' },
]

export const FRECUENCIAS = [
  { id: '15m', label: 'Cada 15 min', ms: 15 * 60 * 1000 },
  { id: '1h', label: 'Cada hora', ms: 60 * 60 * 1000 },
  { id: '6h', label: 'Cada 6 horas', ms: 6 * 60 * 60 * 1000 },
  { id: '1d', label: 'Diaria', ms: 24 * 60 * 60 * 1000 },
  { id: '1w', label: 'Semanal', ms: 7 * 24 * 60 * 60 * 1000 },
  { id: '1M', label: 'Mensual', ms: 30 * 24 * 60 * 60 * 1000 },
]

// ═══════════════════════════════════════════════════════════════
// HELPERS DE REFERENCIA
// ═══════════════════════════════════════════════════════════════

const SUB = 'tareas'
const SUB_SEÑALES = 'señales'
const SUB_REGLAS = 'reglas'
const SUB_COMENTARIOS = 'comentarios'

// ─── ¿La empresa puede tener Tareas? (ADR 2026-10-08, tareas y agentes) ─────
// Espejo de `functions/lib/tareasRefs.js` (paridad en `tareas.paridad.test.js`).
// `asignaciones` es el id del módulo Tareas en el catálogo. Contratado en el
// plan Y no apagado en la solapa Módulos de la cuenta; devuelve la causa.
export const MODULO_TAREAS = 'asignaciones'
export const estadoTareasEn = (empresa) => estadoOperable(empresa, MODULO_TAREAS)

// La causa REAL en el mensaje: "no está en el plan" no es "la cuenta lo apagó"
// ni "no se pudo leer" (ADR 2026-10-08 D4).
export const MENSAJES_TAREAS_NO_OPERABLES = Object.freeze({
  'fuera-del-plan': 'Tareas no está incluido en el plan de esta cuenta',
  'plan-sin-sincronizar': 'Esta cuenta no tiene su plan aplicado: Tareas queda apagado hasta sincronizar los módulos',
  'apagado-por-la-cuenta': 'Tareas está apagado en los módulos de esta cuenta',
  'ilegible': 'No se pudo verificar si esta cuenta tiene Tareas',
  'empresa-inexistente': 'La empresa no existe',
})

/** El mensaje de una causa; una causa desconocida dice «no se pudo verificar», nunca nada. */
export const mensajeTareasNoOperables = (causa) => MENSAJES_TAREAS_NO_OPERABLES[causa] || MENSAJES_TAREAS_NO_OPERABLES.ilegible

/**
 * El aviso para el STAFF de por qué no hay tareas ni agentes en esta cuenta
 * (molde de «No incluida en el plan»: apaga con la causa, no desaparece). El
 * cliente no ve nada: para él, esto no existe en su plan.
 *
 * @returns {string|null}
 */
export function avisoTareasApagadas({ tareasOperables, causa, esStaff }) {
  if (tareasOperables || !esStaff) return null
  return `Tareas y agentes apagados en esta cuenta: ${mensajeTareasNoOperables(causa)}.`
}

/**
 * PURO. El aviso para el STAFF en la solapa Módulos de una cuenta sin el plan
 * aplicado (report JYdnQZaaM8uNgu6BHNev). Esa solapa usa el default de
 * VISIBILIDAD —sin lista, se ven todos los módulos, y prendidos— mientras que
 * lo que depende del plan (Tareas y agentes por `estadoOperable`, conectar
 * canales y los dashboards de canal por `moduloContratadoPor`) queda apagado:
 * sin esto, dos solapas de la misma ficha afirmaban lo contrario. No cambia el
 * default; dice la causa. Con el plan aplicado las dos solapas ya coinciden.
 *
 * @returns {string|null}
 */
export function avisoPlanSinAplicar({ empresa, esStaff }) {
  if (!esStaff) return null
  const { causa } = estadoTareasEn(empresa)
  if (causa !== CAUSAS_OPERABLE.PLAN_SIN_SINCRONIZAR) return null
  return `${mensajeTareasNoOperables(causa)}. Tampoco se pueden conectar canales ni ver los dashboards que dependen del plan. Los módulos de abajo se muestran todos por compatibilidad; el plan se aplica sincronizando sus módulos desde Plataforma → Planes.`
}

/**
 * PURO. El aviso del alta de una empresa desde el listado, que activa los
 * agentes de detección por default (report 5hsRZYBzgA4H1GeKoZjq). Decía «N
 * agentes activados» aunque la empresa nace sin el plan aplicado y ninguno
 * puede correr: la configuración queda lista, pero el mensaje dice la causa
 * real. Con el alta de hoy (sólo `planId`/`plan`, sin `modulosDisponibles`)
 * siempre cae en la rama no operable; la otra queda para el día que el alta
 * aplique el plan, y la cubre un test.
 *
 * @param {{ activados: number, empresa: object }} p  `empresa`: el doc tal cual se escribió
 * @returns {{ tipo: 'success'|'info', texto: string }}
 */
export function avisoEmpresaCreada({ activados, empresa }) {
  if (!(activados > 0)) return { tipo: 'success', texto: 'Empresa creada' }
  const n = `${activados} agente${activados > 1 ? 's' : ''} de detección`
  const { operable, causa } = estadoTareasEn(empresa)
  if (operable) return { tipo: 'success', texto: `Empresa creada — ${n} activado${activados > 1 ? 's' : ''} (modo manual)` }
  return { tipo: 'info', texto: `Empresa creada — ${n} configurado${activados > 1 ? 's' : ''} (modo manual), sin correr todavía: ${mensajeTareasNoOperables(causa)}` }
}

// Cache corta por empresa: una corrida de agentes crea decenas de tareas y no
// tiene por qué releer la empresa en cada una. 60 s, igual que el cache de los
// interruptores del servidor. Un error de lectura NO se cachea.
const _tareasOperablesCache = new Map()
const TAREAS_OPERABLES_TTL_MS = 60_000

/**
 * Tira si la empresa no puede tener Tareas (plan + solapa Módulos). Es la
 * puerta del navegador para todo lo que crea o suma a una tarea: agentes,
 * "crear tarea", Mensajes, Señales, invitaciones a proyecto, macros. Las
 * rules de `empresas/{e}/tareas` son el candado de fondo; esto da el motivo
 * legible antes de que el write rebote. El error trae `causa`.
 *
 * @param {string} empresaId
 * @returns {Promise<void>}
 */
export async function exigirTareasOperables(empresaId) {
  const ahora = Date.now()
  const enCache = _tareasOperablesCache.get(empresaId)
  let estado = enCache && ahora - enCache.ts < TAREAS_OPERABLES_TTL_MS ? enCache.estado : null
  if (!estado) {
    let snap
    try {
      snap = await getDoc(doc(db, COLLECTIONS.EMPRESAS, empresaId))
    } catch (_e) {
      const err = new Error(MENSAJES_TAREAS_NO_OPERABLES.ilegible)
      err.causa = 'ilegible'
      throw err
    }
    estado = snap.exists() ? estadoTareasEn(snap.data()) : { operable: false, causa: 'empresa-inexistente' }
    _tareasOperablesCache.set(empresaId, { estado, ts: ahora })
  }
  if (estado.operable) return
  const err = new Error(mensajeTareasNoOperables(estado.causa))
  err.causa = estado.causa
  throw err
}

/**
 * Vacía el cache de `exigirTareasOperables`. Lo llama la ficha al guardar la
 * solapa Módulos: si no, al volver a prender Tareas la creación podía seguir
 * rebotando hasta 60 s con el estado viejo.
 */
export function olvidarEstadoTareas() { _tareasOperablesCache.clear() }

function accionablesRef(empresaId) {
  return collection(db, COLLECTIONS.EMPRESAS, empresaId, SUB)
}
function accionableRef(empresaId, accId) {
  return doc(db, COLLECTIONS.EMPRESAS, empresaId, SUB, accId)
}
function señalesRef(empresaId) {
  return collection(db, COLLECTIONS.EMPRESAS, empresaId, SUB_SEÑALES)
}
function señalRef(empresaId, señalId) {
  return doc(db, COLLECTIONS.EMPRESAS, empresaId, SUB_SEÑALES, señalId)
}
function reglasRef(empresaId) {
  return collection(db, COLLECTIONS.EMPRESAS, empresaId, SUB_REGLAS)
}
function reglaRef(empresaId, reglaId) {
  return doc(db, COLLECTIONS.EMPRESAS, empresaId, SUB_REGLAS, reglaId)
}
function comentariosRef(empresaId, accId) {
  return collection(db, COLLECTIONS.EMPRESAS, empresaId, SUB, accId, SUB_COMENTARIOS)
}

// ═══════════════════════════════════════════════════════════════
// CRUD — ACCIONABLES
// ═══════════════════════════════════════════════════════════════

/**
 * Listar accionables de una empresa con filtros opcionales.
 */
export async function getAccionables(empresaId, filtros = {}) {
  const constraints = []
  if (filtros.estado) constraints.push(where('estado', '==', filtros.estado))
  if (filtros.responsableUserId) constraints.push(where('responsableUserId', '==', filtros.responsableUserId))
  if (filtros.asignadoA) constraints.push(where('asignadoA', '==', filtros.asignadoA))

  const q = constraints.length > 0
    ? query(accionablesRef(empresaId), ...constraints, orderBy('creadoEn', 'desc'))
    : query(accionablesRef(empresaId), orderBy('creadoEn', 'desc'))

  const snap = await getDocs(q)
  return snap.docs.map(docConId)
}

/**
 * Vista consultor: accionables de múltiples empresas (collectionGroup).
 */
export async function getAccionablesGlobal(empresaIds, filtros = {}) {
  const constraints = []
  if (empresaIds?.length > 0 && empresaIds.length <= 10) {
    constraints.push(where('empresaId', 'in', empresaIds))
  }
  if (filtros.estado) constraints.push(where('estado', '==', filtros.estado))

  const q = constraints.length > 0
    ? query(collectionGroup(db, SUB), ...constraints)
    : collectionGroup(db, SUB)

  const snap = await getDocs(q)
  const list = snap.docs.map(docConId)
  list.sort((a, b) => (b.creadoEn?.toMillis?.() || 0) - (a.creadoEn?.toMillis?.() || 0))
  return list
}

/**
 * Obtener un accionable por ID.
 */
export async function getAccionable(empresaId, accId) {
  const snap = await getDoc(accionableRef(empresaId, accId))
  return snap.exists() ? docConId(snap) : null
}

/**
 * Crear un accionable unificado.
 *
 * Soporta ambos patrones de creación:
 *   - Desde consultoría/mensaje: emisorUserId + responsableUserId + estado PROPUESTO
 *   - Desde dashboard/regla:     asignadoPor + asignadoA + estado PENDIENTE
 *
 * @param {string} empresaId
 * @param {Object} data — campos del accionable
 * @returns {string} ID del accionable creado
 */
// ═══════════════════════════════════════════════════════════════
// MERGE / ADDON de recursos por origen (Etapa 1, 13-jul-2026)
// ───────────────────────────────────────────────────────────────
// Sube recursos a la tarea activa de un tipoAccion, mergeando por origen (la
// lógica pura vive en mergeRecursos.js). Si no hay tarea activa, crea una. El
// merge va en TRANSACCIÓN: las tres fuentes (P&R, Opiniones, Postventa) pueden
// correr a la vez sin pisarse el array de recursos. Reemplaza el "saltear"
// del dedup viejo — ahora las fuentes SUMAN en una sola tarea por publicación.

/**
 * @param {string} empresaId
 * @param {Object} args
 * @param {string} args.tipoAccion  — clave de dedupe (ej. 'modificar_publicacion')
 * @param {string} args.origen      — 'preguntas' | 'opiniones' | 'postventa' | 'manual' | area
 * @param {Array}  args.recursos    — recursos entrantes (con hallazgos/metricas)
 * @param {Object} [args.baseData]  — datos para crearAccionable si no hay tarea activa
 * @returns {Promise<{ tareaId: string|null, creada: boolean, agregados: number, actualizados: number }>}
 */
export async function upsertRecursosEnTarea(empresaId, { tipoAccion, origen, recursos, baseData = null, estadosActivos = ESTADOS_TAREA_ACTIVA } = {}) {
  if (!empresaId || !tipoAccion || !origen) {
    throw new Error('[upsertRecursosEnTarea] empresaId, tipoAccion y origen requeridos')
  }
  await exigirTareasOperables(empresaId)
  const entrantes = Array.isArray(recursos) ? recursos : []
  if (entrantes.length === 0) return { creadas: 0, agregados: 0, actualizados: 0, tareas: [] }

  // Todas las tareas activas de este tipoAccion (estado filtrado en JS para no
  // depender de un índice compuesto tipoAccion+estado).
  const snap = await getDocs(query(accionablesRef(empresaId), where('tipoAccion', '==', tipoAccion)))
  const activas = snap.docs
    .filter(d => estadosActivos.includes(d.data().estado))
    .sort((a, b) => (a.data().creadoEn?.seconds || 0) - (b.data().creadoEn?.seconds || 0))

  // itemId -> tareaId que YA lo contiene (la más antigua gana). El merge es
  // POR PUBLICACIÓN: cada recurso entrante se suma SOLO a la tarea que ya tiene
  // esa misma pub. Nunca a otra (bug 13-jul: metía pubs distintas en la 1a tarea).
  const tareaPorItem = new Map()
  for (const d of activas) {
    for (const r of (d.data().recursos || [])) {
      if (r?.itemId != null) { const k = String(r.itemId); if (!tareaPorItem.has(k)) tareaPorItem.set(k, d.id) }
    }
  }

  const porTarea = new Map()   // tareaId -> recursos[] a mergear
  const sinTarea = []          // recursos sin tarea activa que los contenga
  for (const ent of entrantes) {
    const id = ent?.itemId != null ? String(ent.itemId) : null
    const destino = id ? tareaPorItem.get(id) : null
    if (destino) {
      if (!porTarea.has(destino)) porTarea.set(destino, [])
      porTarea.get(destino).push(ent)
    } else {
      sinTarea.push(ent)
    }
  }

  let agregados = 0, actualizados = 0, creadas = 0
  const tareas = []

  // Merge en cada tarea que ya contiene la pub — transacción por tarea.
  for (const [tareaId, recs] of porTarea.entries()) {
    const ref = doc(accionablesRef(empresaId), tareaId)
    await runTransaction(db, async (tx) => {
      const cur = await tx.get(ref)
      if (!cur.exists()) return
      const merged = mergearRecursosPorOrigen(cur.data().recursos || [], recs, origen)
      agregados += merged.agregados; actualizados += merged.actualizados
      tx.update(ref, { recursos: merged.recursos, actualizadoEn: serverTimestamp() })
    })
    tareas.push(tareaId)
  }

  // Pubs sin tarea que las contenga -> tarea nueva (solo si el caller dio baseData).
  if (sinTarea.length > 0 && baseData) {
    const recursosNuevos = sinTarea.map(r => recursoConOrigen(r, origen))
    const tareaId = await crearAccionable(empresaId, { ...baseData, tipoAccion, recursos: recursosNuevos })
    creadas = 1; agregados += recursosNuevos.length; tareas.push(tareaId)
  }

  return { creadas, agregados, actualizados, tareas, sinTareaSinCrear: baseData ? 0 : sinTarea.length }
}

export async function crearAccionable(empresaId, data) {
  await exigirTareasOperables(empresaId)
  const ref = doc(accionablesRef(empresaId))
  const ahora = new Date().toISOString()

  // Normalizar personas — soportar ambas convenciones
  const emisor = data.emisorUserId || data.asignadoPor || null
  const responsable = data.responsableUserId || data.asignadoA || null

  // ¿Requiere aceptación? Si viene de mensaje/ClarIA → PROPUESTO, si no → PENDIENTE
  const requiereAceptacion = data.estado === ACCIONABLE_ESTADOS.PROPUESTO
    || data.requiereAceptacion === true
    || (data.origen?.tipo === 'mensaje')
    || (data.origen?.tipo === 'reunion_ia')
    || (data.origen?.tipo === 'reunion_auto_ia')

  const estadoInicial = data.estado || (requiereAceptacion ? 'propuesto' : 'pendiente')

  const acc = {
    empresaId,
    // ── Personas ──
    emisorUserId: emisor,
    responsableUserId: responsable,
    asignadoA: responsable,            // alias para compat
    asignadoPor: emisor,               // alias para compat
    responsableAnterior: null,
    ownerUserId: data.ownerUserId || emisor,
    participantes: Array.from(new Set([emisor, responsable].filter(Boolean))),
    // ── Estado ──
    estado: estadoInicial,
    prioridad: data.prioridad || 'normal',
    urgenciaAuto: data.urgenciaAuto || 'normal',
    vencimiento: data.vencimiento || null,
    deadline: data.deadline || null,
    slaId: data.slaId || null,
    // ── Tipo de tarea y SLA (catálogo) ──
    tipoTareaId: data.tipoTareaId || null,  // FK a tiposTarea — se resuelve abajo si hay tipoAccion
    slaMinutos: data.slaMinutos ?? null,     // SLA resuelto (cascada). Se calcula abajo.
    ventanaAlertaMin: data.ventanaAlertaMin ?? null,
    // ── Tracking de tiempos (automático por cambio de estado) ──
    trackingActivo: false,
    intervalos: [],
    tiempoRealMinutos: null,
    iniciadoEn: null,
    completadoEn: null,
    // ── Ejecutor: humano o agente ──
    ejecutorTipo: data.ejecutorTipo || 'humano',  // 'humano' | 'agente'
    ejecutorId: data.ejecutorId || responsable,    // userId o agenteId
    // ── Contenido ──
    titulo: data.titulo || '',
    descripcion: data.descripcion || '',
    tipoAccion: data.tipoAccion || null,
    // ── Origen ──
    origen: data.origen || 'manual',
    origenDashboard: data.origenDashboard || null,
    area: data.area || null,
    señalId: data.señalId || null,
    // ── Vínculo con módulo Casos (B4.2, mayo 2026) ──
    // Si la tarea nace desde un caso (vía AbrirCasoButton o action manual),
    // se setea casoId. Tras crear la tarea, el caller debe llamar a
    // vincularTareaHija(casoId, tareaId) desde @core/lib/casos para mantener
    // el array tareasHijas[] del caso sincronizado.
    casoId: data.casoId || null,
    // ── Recursos ──
    recursos: data.recursos || null,
    recurso: data.recurso || null,
    // ── Adjuntos de la tarea (16-jul-2026 — tarea cargar-datos de Productos) ──
    // [{ nombre, url, tipo, tamano }] — archivos ya subidos a Storage que
    // acompañan la tarea desde su creación (ej. el CSV pre-llenado para
    // completar costos). Los render: AsignacionesPage + AsignacionDetallePage.
    adjuntos: Array.isArray(data.adjuntos) && data.adjuntos.length > 0 ? data.adjuntos : null,
    // ── Datos extra por tipo de tarea ──
    datosCompetencia: data.datosCompetencia || null,  // { urlPublicacion, competidores[] }
    // ── Delegación y escalamiento ──
    delegaciones: 0,
    escalamiento: data.escalamiento || {
      tipo: 'notifica_y_escala',
      escalaA: emisor,
      recordatorioHoras: 24,
    },
    // ── Verificación ──
    requiereVerificacion: data.requiereVerificacion || false,
    verificada: false,
    // ── Flags ──
    iaResuelta: false,
    motivoRechazo: null,
    motivoPausa: null,
    // ── Timestamps ──
    aceptadoEn: null,
    completadaEn: null,
    cerradoEn: null,
    canceladaEn: null,
    // ── Historial ──
    historial: [{
      accion: 'creada',
      porUserId: emisor,
      por: emisor,
      a: responsable,
      fecha: ahora,
      en: ahora,
      nota: data.notaInicial || null,
    }],
    // ── Timestamps de sistema ──
    creadoEn: serverTimestamp(),
    actualizadoEn: serverTimestamp(),
  }

  // ── Resolver SLA del catálogo si hay tipoAccion y no viene slaMinutos explícito ──
  if (acc.tipoAccion && acc.slaMinutos == null) {
    try {
      const tipoDoc = await getTipoTareaPorCodigo(acc.tipoAccion)
      if (tipoDoc) {
        acc.tipoTareaId = tipoDoc.id
        const sla = await resolverSLA(empresaId, tipoDoc.id, responsable)
        if (sla) {
          acc.slaMinutos = sla.slaMinutos
          acc.ventanaAlertaMin = sla.ventanaAlertaMinutos
        }
      }
    } catch (e) { console.warn('[crearAccionable] resolverSLA falló:', e) }
  }

  await setDoc(ref, acc)

  // Notificar al responsable
  if (responsable && responsable !== emisor) {
    try {
      await notificar({
        userId: responsable,
        tipo: NOTIFICACION_TIPOS.TAREA_ASIGNADA,
        titulo: requiereAceptacion ? 'Te propusieron una tarea' : 'Te asignaron una tarea',
        cuerpo: acc.titulo,
        link: linkTareaNotificacion(empresaId, ref.id),
        recursoTipo: 'accionable',
        recursoId: ref.id,
        empresaId,
        prioridad: NOTIFICACION_PRIORIDADES.NORMAL,
        metadatos: { origen: acc.origen, area: acc.area },
      })
    } catch (e) { console.warn('[crearAccionable] notif falló:', e) }
  }

  // B4.2 — Vincular bidireccional con Caso si la tarea nació desde uno.
  // Lazy import para evitar cycle tareas ↔ casos (dependencia circular).
  if (acc.casoId) {
    try {
      const { vincularTareaHija } = await import('./casos')
      await vincularTareaHija(acc.casoId, ref.id, {
        titulo: acc.titulo,
        tipoTarea: acc.tipoAccion || null,
      })
    } catch (e) {
      console.warn('[crearAccionable] vincularTareaHija falló:', e?.message)
    }
  }

  return ref.id
}

/**
 * Actualizar campos de un accionable (update parcial).
 */
export async function actualizarAccionable(empresaId, accId, data) {
  const { id: _id, creadoEn, historial, ...safe } = data
  // Sincronizar aliases de personas
  if (safe.responsableUserId && !safe.asignadoA) safe.asignadoA = safe.responsableUserId
  if (safe.asignadoA && !safe.responsableUserId) safe.responsableUserId = safe.asignadoA
  if (safe.emisorUserId && !safe.asignadoPor) safe.asignadoPor = safe.emisorUserId
  if (safe.asignadoPor && !safe.emisorUserId) safe.emisorUserId = safe.asignadoPor
  await updateDoc(accionableRef(empresaId, accId), {
    ...safe,
    actualizadoEn: serverTimestamp(),
  })
}

/**
 * Eliminar un accionable.
 */
export async function eliminarAccionable(empresaId, accId) {
  await deleteDoc(accionableRef(empresaId, accId))
}

// ═══════════════════════════════════════════════════════════════
// FLUJO DE ACEPTACIÓN / RECHAZO
// ═══════════════════════════════════════════════════════════════

/**
 * Aceptar una tarea propuesta o transferida.
 * Requiere que userId sea el responsable actual.
 */
export async function aceptarAccionable(empresaId, accId, userId) {
  const ref = accionableRef(empresaId, accId)
  const snap = await getDoc(ref)
  if (!snap.exists()) throw new Error('Accionable no encontrado')
  const a = docConId(snap)

  const resp = a.responsableUserId || a.asignadoA
  if (resp !== userId) {
    const err = new Error('Solo el responsable actual puede aceptar esta tarea.')
    err.code = 'accionables/no-responsable'
    throw err
  }
  if (a.estado !== 'propuesto' && a.estado !== 'transferido') {
    return a // no-op si ya está aceptada/cerrada
  }

  await updateDoc(ref, forUpdate({
    estado: 'pendiente',
    aceptadoEn: serverTimestamp(),
    historial: arrayUnion({
      accion: 'aceptado',
      porUserId: userId,
      por: userId,
      en: new Date().toISOString(),
      fecha: new Date().toISOString(),
    }),
  }))

  // Notificar al emisor
  const emisor = a.emisorUserId || a.asignadoPor
  if (emisor) {
    try {
      await notificar({
        userId: emisor,
        tipo: NOTIFICACION_TIPOS.TAREA_ACEPTADA,
        titulo: 'Tu tarea fue aceptada',
        cuerpo: a.titulo,
        link: linkTareaNotificacion(empresaId, a.id),
        recursoTipo: 'accionable',
        recursoId: a.id,
        empresaId,
        prioridad: NOTIFICACION_PRIORIDADES.NORMAL,
      })
    } catch (e) { console.warn('[aceptarAccionable] notif falló:', e) }
  }

  return { ...a, estado: 'pendiente' }
}

/**
 * Rechazar una tarea: vuelve al emisor (o al responsableAnterior si fue transferida).
 */
export async function rechazarAccionable(empresaId, accId, userId, { motivo = '' } = {}) {
  const ref = accionableRef(empresaId, accId)
  const snap = await getDoc(ref)
  if (!snap.exists()) throw new Error('Accionable no encontrado')
  const a = docConId(snap)

  const resp = a.responsableUserId || a.asignadoA
  if (resp !== userId) {
    const err = new Error('Solo el responsable actual puede rechazar la tarea.')
    err.code = 'accionables/no-responsable'
    throw err
  }

  const emisor = a.emisorUserId || a.asignadoPor
  const vuelveA = a.estado === 'transferido' && a.responsableAnterior
    ? a.responsableAnterior
    : emisor

  await updateDoc(ref, forUpdate({
    estado: 'rechazado',
    responsableAnterior: resp,
    responsableUserId: vuelveA,
    asignadoA: vuelveA,
    participantes: arrayUnion(vuelveA),
    motivoRechazo: motivo || null,
    historial: arrayUnion({
      accion: 'rechazado',
      porUserId: userId,
      por: userId,
      vuelveA,
      motivo,
      en: new Date().toISOString(),
      fecha: new Date().toISOString(),
    }),
  }))

  if (vuelveA) {
    try {
      await notificar({
        userId: vuelveA,
        tipo: NOTIFICACION_TIPOS.TAREA_RECHAZADA,
        titulo: 'Te rechazaron una tarea',
        cuerpo: `${a.titulo}${motivo ? ` — Motivo: ${motivo}` : ''}`,
        link: linkTareaNotificacion(empresaId, a.id),
        recursoTipo: 'accionable',
        recursoId: a.id,
        empresaId,
        prioridad: NOTIFICACION_PRIORIDADES.ALTA,
      })
    } catch (e) { console.warn('[rechazarAccionable] notif falló:', e) }
  }

  return { ...a, estado: 'rechazado', responsableUserId: vuelveA }
}

// ═══════════════════════════════════════════════════════════════
// TRANSFERENCIA Y ESCALAMIENTO
// ═══════════════════════════════════════════════════════════════

/**
 * Transferir (escalar) una tarea a otro responsable con aceptación.
 */
export async function transferirAccionable(empresaId, accId, { deUserId, aUserId, motivo = '' }) {
  if (!aUserId) throw new Error('aUserId requerido')
  if (aUserId === deUserId) throw new Error('No tiene sentido transferirse a uno mismo')
  const ref = accionableRef(empresaId, accId)
  const snap = await getDoc(ref)
  if (!snap.exists()) throw new Error('Accionable no encontrado')
  const a = docConId(snap)
  const respActual = a.responsableUserId || a.asignadoA

  await updateDoc(ref, forUpdate({
    estado: 'transferido',
    responsableAnterior: respActual,
    responsableUserId: aUserId,
    asignadoA: aUserId,
    participantes: arrayUnion(aUserId),
    historial: arrayUnion({
      accion: 'transferido',
      porUserId: deUserId,
      por: deUserId,
      de: respActual,
      a: aUserId,
      motivo,
      en: new Date().toISOString(),
      fecha: new Date().toISOString(),
    }),
  }))

  try {
    await notificar({
      userId: aUserId,
      tipo: NOTIFICACION_TIPOS.TAREA_ASIGNADA,
      titulo: 'Te transfirieron una tarea',
      cuerpo: `${a.titulo}${motivo ? ` — ${motivo}` : ''}`,
      link: linkTareaNotificacion(empresaId, a.id),
      recursoTipo: 'accionable',
      recursoId: a.id,
      empresaId,
      prioridad: NOTIFICACION_PRIORIDADES.ALTA,
      metadatos: { de: respActual, motivo },
    })
  } catch (e) { console.warn('[transferirAccionable] notif falló:', e) }

  return { ...a, estado: 'transferido', responsableUserId: aUserId }
}

/**
 * Delegar sin aceptación (reasignación directa), con control de máximo.
 */
export async function delegarAccionable(empresaId, accId, { por, a, nota, maxDelegaciones = 3 }) {
  const snap = await getDoc(accionableRef(empresaId, accId))
  if (!snap.exists()) throw new Error('Accionable no encontrado')
  if ((snap.data().delegaciones || 0) >= maxDelegaciones) {
    throw new Error(`Máximo de delegaciones alcanzado (${maxDelegaciones})`)
  }
  await agregarMovimiento(empresaId, accId, { accion: 'delegada', por, a, nota })
}

// ═══════════════════════════════════════════════════════════════
// CAMBIOS DE ESTADO
// ═══════════════════════════════════════════════════════════════

/**
 * Avanzar el estado operativo. Soporta todos los estados válidos.
 */
export async function cambiarEstadoAccionable(empresaId, accId, nuevoEstado, { porUserId, nota = '' } = {}) {
  const valid = new Set([
    'propuesto', 'pendiente', 'no_iniciada', 'en_curso', 'en_revision',
    'cerrado', 'pausada', 'vencida', 'cancelada',
  ])
  if (!valid.has(nuevoEstado)) throw new Error(`Estado no permitido: ${nuevoEstado}`)
  const ref = accionableRef(empresaId, accId)

  // Leer estado actual para calcular tracking de tiempos
  const snap = await getDoc(ref)
  const tareaActual = snap.exists() ? snap.data() : {}

  const updates = {
    estado: nuevoEstado,
    historial: arrayUnion({
      accion: `estado:${nuevoEstado}`,
      porUserId: porUserId || null,
      por: porUserId || null,
      nota,
      en: new Date().toISOString(),
      fecha: new Date().toISOString(),
    }),
  }
  if (nuevoEstado === 'cerrado') updates.cerradoEn = serverTimestamp()
  if (nuevoEstado === 'cerrado') updates.completadaEn = serverTimestamp()
  if (nuevoEstado === 'pausada') updates.motivoPausa = nota || null
  if (nuevoEstado === 'cancelada') updates.canceladaEn = serverTimestamp()
  if (nuevoEstado === 'en_curso' || nuevoEstado === 'pendiente') updates.motivoPausa = null

  // ── Tracking automático de tiempos ──
  const trackingUpdates = calcularTrackingUpdates(tareaActual, nuevoEstado)
  Object.assign(updates, trackingUpdates)

  await updateDoc(ref, forUpdate(updates))
}

/**
 * Reabrir una tarea cerrada o cancelada (vuelve a PENDIENTE).
 */
export async function reabrirAccionable(empresaId, accId, porUserId) {
  const ref = accionableRef(empresaId, accId)
  await updateDoc(ref, forUpdate({
    estado: 'pendiente',
    cerradoEn: null,
    completadaEn: null,
    completadoEn: null,
    canceladaEn: null,
    resultadoAgente: null,
    progresoAgente: null,
    ejecutorTipo: null,
    ejecutorId: null,
    historial: arrayUnion({
      accion: 'reabierto',
      porUserId: porUserId || null,
      por: porUserId || null,
      en: new Date().toISOString(),
      fecha: new Date().toISOString(),
    }),
  }))
}

/**
 * Agregar un movimiento genérico al historial + actualizar estado/asignadoA.
 */
export async function agregarMovimiento(empresaId, accId, movimiento) {
  const ref = accionableRef(empresaId, accId)
  const snap = await getDoc(ref)
  if (!snap.exists()) throw new Error('Accionable no encontrado')

  const data = snap.data()
  const historial = [...(data.historial || []), {
    ...movimiento,
    fecha: new Date().toISOString(),
    en: new Date().toISOString(),
  }]
  const updates = { historial, actualizadoEn: serverTimestamp() }

  // Actualizar responsable si es delegación/reasignación
  if (movimiento.accion === 'delegada' || movimiento.accion === 'reasignada') {
    updates.responsableUserId = movimiento.a
    updates.asignadoA = movimiento.a
    updates.delegaciones = (data.delegaciones || 0) + 1
    if (movimiento.a) updates.participantes = arrayUnion(movimiento.a)
  }
  // Actualizar estado según acción
  const estadoMap = {
    en_progreso: 'en_curso',
    en_curso: 'en_curso',
    completada: 'cerrado',
    pausada: 'pausada',
    reanudada: 'en_curso',
    cancelada: 'cancelada',
    vencida: 'vencida',
  }
  if (estadoMap[movimiento.accion]) {
    updates.estado = estadoMap[movimiento.accion]
  }
  if (movimiento.accion === 'completada' || movimiento.accion === 'cerrado') {
    updates.completadaEn = serverTimestamp()
    updates.cerradoEn = serverTimestamp()
  }
  if (movimiento.accion === 'pausada') updates.motivoPausa = movimiento.nota || null
  if (movimiento.accion === 'reanudada') updates.motivoPausa = null

  await updateDoc(ref, updates)
}

// ═══════════════════════════════════════════════════════════════
// COMENTARIOS
// ═══════════════════════════════════════════════════════════════

export async function getComentarios(empresaId, accId) {
  const q = query(comentariosRef(empresaId, accId), orderBy('creadoEn', 'asc'))
  const snap = await getDocs(q)
  return snap.docs.map(docConId)
}

export async function agregarComentario(empresaId, accId, { autorId, autorNombre, texto, adjuntos }) {
  const ref = await addDoc(comentariosRef(empresaId, accId), {
    autorId,
    autorNombre,
    texto,
    adjuntos: adjuntos || [],
    creadoEn: serverTimestamp(),
  })
  return ref.id
}

// ═══════════════════════════════════════════════════════════════
// SEÑALES
// ═══════════════════════════════════════════════════════════════

export async function getSeñales(empresaId, filtros = {}) {
  const constraints = []
  if (filtros.estado) constraints.push(where('estado', '==', filtros.estado))

  const q = constraints.length > 0
    ? query(señalesRef(empresaId), ...constraints, orderBy('creadoEn', 'desc'))
    : query(señalesRef(empresaId), orderBy('creadoEn', 'desc'))

  const snap = await getDocs(q)
  return snap.docs.map(docConId)
}

export async function getSeñalesGlobal(empresaIds) {
  const constraints = [where('estado', '==', 'pendiente')]
  if (empresaIds?.length > 0 && empresaIds.length <= 10) {
    constraints.push(where('empresaId', 'in', empresaIds))
  }
  const q = query(collectionGroup(db, SUB_SEÑALES), ...constraints)
  const snap = await getDocs(q)
  const list = snap.docs.map(docConId)
  list.sort((a, b) => (b.creadoEn?.toMillis?.() || 0) - (a.creadoEn?.toMillis?.() || 0))
  return list
}

export async function aprobarSeñal(empresaId, señalId, { resueltaPor, accionableData }) {
  const accId = await crearAccionable(empresaId, { ...accionableData, señalId, origen: 'regla' })
  await updateDoc(señalRef(empresaId, señalId), {
    estado: 'aprobada',
    resueltaPor,
    resueltaEn: serverTimestamp(),
    accionableId: accId,
  })
  return accId
}

export async function descartarSeñal(empresaId, señalId, { resueltaPor, feedbackUtil }) {
  await updateDoc(señalRef(empresaId, señalId), {
    estado: 'descartada',
    resueltaPor,
    resueltaEn: serverTimestamp(),
    feedbackUtil: feedbackUtil ?? null,
  })
}

// ═══════════════════════════════════════════════════════════════
// REGLAS DE NEGOCIO
// ═══════════════════════════════════════════════════════════════

export async function getReglas(empresaId) {
  const snap = await getDocs(query(reglasRef(empresaId), orderBy('nombre')))
  return snap.docs.map(docConId)
}

export async function crearRegla(empresaId, data) {
  const ref = doc(reglasRef(empresaId))
  await setDoc(ref, {
    ...data,
    activa: data.activa ?? true,
    esDefault: false,
    ultimaEjecucion: null,
    creadoEn: serverTimestamp(),
    actualizadoEn: serverTimestamp(),
  })
  return ref.id
}

export async function actualizarRegla(empresaId, reglaId, data) {
  const { id: _id, creadoEn, ...safe } = data
  await updateDoc(reglaRef(empresaId, reglaId), { ...safe, actualizadoEn: serverTimestamp() })
}

export async function eliminarRegla(empresaId, reglaId) {
  await deleteDoc(reglaRef(empresaId, reglaId))
}

/** Seed reglas default de un módulo para una empresa */
export async function seedReglasDefault(empresaId, reglasDefault = []) {
  const existing = await getReglas(empresaId)
  const existingOrigins = existing.map(r => r.reglaOrigenId).filter(Boolean)
  const batch = writeBatch(db)
  let created = 0

  for (const regla of reglasDefault) {
    if (existingOrigins.includes(regla.id)) continue
    const ref = doc(reglasRef(empresaId))
    batch.set(ref, {
      ...regla,
      reglaOrigenId: regla.id,
      activa: true,
      esDefault: true,
      ultimaEjecucion: null,
      creadoEn: serverTimestamp(),
      actualizadoEn: serverTimestamp(),
    })
    created++
  }
  if (created > 0) await batch.commit()
  return created
}

// ═══════════════════════════════════════════════════════════════
// AUTO-ASIGNACIÓN INTELIGENTE
// ═══════════════════════════════════════════════════════════════

// Report qWgfCJ4aKEr7y29ZEiXw: `usuarios.suspendido` es un campo que nadie
// escribe — el estado real vive en `usuarios.estado`. Espejo de
// `ESTADOS_NO_DELEGABLES` (functions/lib/estadosUsuario.js, módulo hoja): no
// se puede importar `functions/` desde el bundle del front, así que se
// mantiene la misma lista a mano acá — atada por el test de paridad
// `tareas.paridad.test.js` (patrón `.claude/patterns/espejo-front-back.md`).
export const ESTADOS_NO_ASIGNABLES = new Set(['suspendido', 'bloqueado', 'invitado', 'invitado_google', 'pendiente'])

export async function empresaTieneAutoAsignacion(empresaId) {
  if (!empresaId) return false
  try {
    const snap = await getDoc(doc(db, COLLECTIONS.EMPRESAS, empresaId))
    return snap.exists() ? !!snap.data().autoAsignacion : false
  } catch { return false }
}

export async function autoAsignar(empresaId, { area = null, tipoAccion = null, excluir = [], skipCheck = false } = {}) {
  if (!empresaId) return null

  if (!skipCheck) {
    const habilitada = await empresaTieneAutoAsignacion(empresaId)
    if (!habilitada) return null
  }

  try {
    const usrSnap = await getDocs(
      query(collection(db, 'usuarios'), where('empresaIds', 'array-contains', empresaId))
    )
    let candidatos = usrSnap.docs
      .map(docConId)
      .filter(u => !ESTADOS_NO_ASIGNABLES.has(u.estado) && !excluir.includes(u.id))

    if (candidatos.length === 0) return null

    if (area) {
      const porArea = candidatos.filter(u =>
        u.area === area || (u.skills || []).includes(area)
      )
      if (porArea.length > 0) candidatos = porArea
    }

    const asigSnap = await getDocs(
      query(accionablesRef(empresaId), where('estado', 'in', ['pendiente', 'en_curso', 'no_iniciada']))
    )
    const cargaPor = {}
    asigSnap.docs.forEach(d => {
      const a = d.data()
      const resp = a.responsableUserId || a.asignadoA
      if (resp) cargaPor[resp] = (cargaPor[resp] || 0) + 1
    })

    const afinidadPor = {}
    if (tipoAccion) {
      const histSnap = await getDocs(
        query(accionablesRef(empresaId), where('tipoAccion', '==', tipoAccion), where('estado', '==', 'cerrado'))
      )
      histSnap.docs.forEach(d => {
        const a = d.data()
        const resp = a.responsableUserId || a.asignadoA
        if (resp) afinidadPor[resp] = (afinidadPor[resp] || 0) + 1
      })
    }

    const scored = candidatos.map(u => {
      const carga = cargaPor[u.id] || 0
      const afinidad = afinidadPor[u.id] || 0
      const score = (afinidad * 2) - carga
      return { ...u, carga, afinidad, score }
    })
    scored.sort((a, b) => b.score - a.score || a.carga - b.carga)
    return scored[0]?.id || null
  } catch (err) {
    console.warn('[autoAsignar] error:', err)
    return null
  }
}

// ═══════════════════════════════════════════════════════════════
// LEGACY — Asignaciones de personas (quién supervisa a quién)
// ═══════════════════════════════════════════════════════════════
// Colección top-level: asignaciones (COLLECTIONS.ASIGNACIONES)
// Tipos:               tiposAsignacion (COLLECTIONS.TIPOS_ASIGNACION)
// El escalado por supervisión que las leía se retiró (ADR 2026-10-21 §4);
// queda el seed de tipos.

const COL_TIPOS_ASIG = COLLECTIONS.TIPOS_ASIGNACION

const TIPOS_DEFAULT = [
  { slug: 'supervisa', nombre: 'Supervisa', descripcion: 'Supervisión directa', direccion: 'arriba', color: '#f59e0b' },
  { slug: 'asesora', nombre: 'Asesora', descripcion: 'Asesoría externa', direccion: 'lateral', color: '#a855f7' },
  { slug: 'opera', nombre: 'Opera', descripcion: 'Operación directa', direccion: 'abajo', color: '#22c55e' },
  { slug: 'coordina', nombre: 'Coordina', descripcion: 'Coordinación de equipo', direccion: 'lateral', color: '#3b82f6' },
]

export async function seedTiposAsignacionDefault({ dryRun = true } = {}) {
  const snap = await getDocs(collection(db, COL_TIPOS_ASIG))
  const existingSlugs = snap.docs.map(d => d.data().slug).filter(Boolean)
  const toCreate = TIPOS_DEFAULT.filter(t => !existingSlugs.includes(t.slug))

  if (dryRun) {
    console.log(`[seedTiposAsignacion] ${toCreate.length} tipos a crear:`, toCreate.map(t => t.slug))
    return toCreate
  }

  const batch = writeBatch(db)
  for (const tipo of toCreate) {
    const ref = doc(collection(db, COL_TIPOS_ASIG))
    batch.set(ref, {
      ...tipo,
      activo: true,
      esDefault: true,
      creadoEn: serverTimestamp(),
      actualizadoEn: serverTimestamp(),
    })
  }
  if (toCreate.length > 0) await batch.commit()
  console.log(`[seedTiposAsignacion] ${toCreate.length} tipos creados`)
  return toCreate
}

// ═══════════════════════════════════════════════════════════════
// NOMBRES DE USUARIO DEL DETALLE — qué uids pinta la pantalla
// ═══════════════════════════════════════════════════════════════
//
// El detalle de una tarea muestra nombres de personas en cuatro lugares:
// responsable, emisor, autores de movimientos (y el destinatario de una
// delegación) y autores de comentarios que no guardaron `autorNombre`.
//
// Esta función existe para que esos uids se lean PUNTUALMENTE
// (`getDoc` por uid) en vez de traer el directorio entero de la
// plataforma: `constraints.md §7.B` prohíbe queries de lista sin filtro
// ni `limit()`, y además la regla de `usuarios` (firestore.rules) sólo es
// satisfacible estáticamente vía `isConsultor()` — para cualquier otro
// perfil la query de lista se rechaza ENTERA, así que el costo se pagaba
// sin resultado. Una lectura por documento sí evalúa `resource.data` y
// resuelve el nombre de los compañeros de empresa (report eOV7sp1SqK0rq2iLfSh6).
//
// Autores que NO son personas: el backend firma movimientos automáticos con
// estos literales (`functions/asignaciones.js:130` y hermanos, `meets.js:2136`).
// Pedirlos a /usuarios no devuelve "no existe" sino permission-denied para
// cualquier no-consultor —la regla evalúa `resource.data` de un doc que no
// está— o sea un warning por cada apertura de tarea tocada por el sistema.
const AUTORES_NO_USUARIO = new Set(['sistema', 'system-ia', 'system'])

// Devuelve uids únicos, sin nulos, en orden estable de aparición.
export function uidsDeTareaDetalle(tarea, comentarios = []) {
  const uids = []
  const push = (uid) => {
    if (typeof uid !== 'string') return
    const v = uid.trim()
    if (!v || AUTORES_NO_USUARIO.has(v) || uids.includes(v)) return
    uids.push(v)
  }

  push(tarea?.asignadoA ?? tarea?.responsableUserId)
  push(tarea?.asignadoPor ?? tarea?.emisorUserId)
  // El historial convive con dos nombres para el autor (`porUserId|por`,
  // documentado en el shape de arriba): los movimientos viejos guardaron uno
  // solo, así que se leen los dos o el nombre queda en uid.
  for (const mov of tarea?.historial || []) { push(mov?.por ?? mov?.porUserId); push(mov?.a) }
  // El comentario que guardó `autorNombre` no necesita lectura: la pantalla
  // ya pinta ese texto y el uid nunca llega a `getNombre`.
  for (const c of comentarios || []) { if (!c?.autorNombre) push(c?.autorId) }

  return uids
}

// ═══════════════════════════════════════════════════════════════
// COMPAT — Re-export aliases para facilitar migración progresiva
// ═══════════════════════════════════════════════════════════════
// Archivos que importaban de asignaciones.js pueden seguir funcionando
// con estos aliases hasta completar la migración.

export const crearAsignacion = crearAccionable
export const getAsignaciones = getAccionables
export const getAsignacionesGlobal = getAccionablesGlobal
export const getAsignacion = getAccionable
export const actualizarAsignacion = actualizarAccionable
export const eliminarAsignacion = eliminarAccionable
export const agregarMovimientoAsignacion = agregarMovimiento
export const delegarAsignacion = delegarAccionable
export const ESTADOS_ASIGNACION = ESTADOS_LISTA

// Exposición en window para debug
if (typeof window !== 'undefined') {
  window.__aceptarAccionable = aceptarAccionable
  window.__rechazarAccionable = rechazarAccionable
  window.__transferirAccionable = transferirAccionable
  window.__crearAccionable = crearAccionable
}
